// TimeSelect.js
import React, { useEffect, useMemo, useRef, useState } from "react";

function useOutsideClick(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener, { passive: true });
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

/**
 * TimeSelect
 * Props:
 *  - value: string 'HH:mm' | '' (clear)
 *  - options: [{value:'HH:mm', label:'1:00PM'}, ...]
 *  - onChange: (newValue) => void
 *  - placeholder: string (default '—')
 *  - disabled: boolean
 *  - error: boolean (adds error ring)
 *  - allowClear: boolean (default true) — show a top "clear" option and allow Backspace/Delete to clear
 */
export function TimeSelect({
  value,
  options,
  onChange,
  placeholder = "—",
  disabled = false,
  error = false,
  allowClear = true,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const btnRef = useRef(null);

  // Inject a "clear" option at the top when allowed
  const optionsWithClear = useMemo(() => {
    return allowClear
      ? [{ value: "", label: placeholder, __clear: true }, ...options]
      : options;
  }, [options, allowClear, placeholder]);

  const selectedIndex = useMemo(
    () => optionsWithClear.findIndex((o) => o.value === value),
    [optionsWithClear, value]
  );

  const [highlighted, setHighlighted] = useState(
    selectedIndex >= 0 ? selectedIndex : 0
  );

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${highlighted}"]`);
      if (el) el.scrollIntoView({ block: "nearest" });
    }
  }, [open, highlighted]);

  useOutsideClick(rootRef, () => setOpen(false));

  const label = useMemo(() => {
    const opt = optionsWithClear.find((o) => o.value === value);
    return opt && !opt.__clear ? opt.label : "";
  }, [optionsWithClear, value]);

  const handleSelect = (idx) => {
    const opt = optionsWithClear[idx];
    if (!opt) return;
    onChange(opt.value); // '' when clearing
    setOpen(false);
    requestAnimationFrame(() => btnRef.current?.focus());
  };

  const onKeyDown = (e) => {
    // Open on ArrowDown/Enter/Space
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    // Quick clear even when closed
    if (!open && (e.key === "Backspace" || e.key === "Delete")) {
      if (allowClear) {
        e.preventDefault();
        onChange("");
      }
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, optionsWithClear.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(highlighted);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
    } else if (e.key === "Backspace" || e.key === "Delete") {
      if (allowClear) {
        e.preventDefault();
        onChange("");
        setOpen(false);
        btnRef.current?.focus();
      }
    }
  };

  return (
    <div className={`cl-time-select ${disabled ? "cl-disabled" : ""}`} ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        className={`cl-time-trigger ${error ? "cl-input-error" : ""}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open ? "true" : "false"}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
      >
        <span className={`cl-time-label ${label ? "" : "cl-time-placeholder"}`}>
          {label || placeholder}
        </span>
        <svg
          className="cl-time-caret"
          viewBox="0 0 20 20"
          width="16"
          height="16"
          aria-hidden="true"
        >
          <path d="M5 7l5 6 5-6" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>

      {open && (
        <ul
          className="cl-time-list"
          role="listbox"
          ref={listRef}
          tabIndex={-1}
          aria-activedescendant={`cl-opt-${highlighted}`}
        >
          {optionsWithClear.map((opt, idx) => {
            const selected = idx === selectedIndex;
            const highlightedClass = idx === highlighted ? "cl-highlight" : "";
            return (
              <li
                id={`cl-opt-${idx}`}
                key={opt.__clear ? "clear" : opt.value}
                data-idx={idx}
                role="option"
                aria-selected={selected ? "true" : "false"}
                className={`cl-time-option ${highlightedClass} ${opt.__clear ? "cl-time-clear" : ""}`}
                onMouseEnter={() => setHighlighted(idx)}
                onMouseDown={(e) => e.preventDefault()} // keep focus
                onClick={() => handleSelect(idx)}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
